import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";
import { guardarSessaoVerComo } from "./verComo";

// Tudo que o painel administrativo lê, num lugar só — o mesmo papel que
// `lib/dados.ts` cumpre para as telas do aluno.
//
// A diferença que importa: aqui NÃO existe regra de acesso em JavaScript. Quem
// decide se a pessoa é operador é o banco (`eh_operador()` dentro de
// `painel_admin()`, `minhas_permissoes()` para o próprio chamador), e o front só
// apresenta o que voltou. Um `if (papel === "admin")` no `.tsx` seria uma
// segunda autorização, que divergiria da primeira no dia em que uma das duas
// mudasse — e a que o aluno consegue burlar é justamente a do navegador.

// `equipe` saiu na 000297: o Suporte virou GRUPO de Admin › Permissões, e o
// único papel de operador que sobrou é o `admin` — o master.
export type Papel = "aluno" | "cliente_final" | "admin";

/** O nível numa aba do painel — a regra é `meu_nivel(área)`, no banco (000295). */
export type NivelDeAcesso = "nenhum" | "ver" | "editar";

/**
 * Quem está logado, do ponto de vista do painel. É espelho de apresentação, não
 * a regra: o banco recusa sozinho, e é por isso que este objeto pode desenhar
 * menu sem virar controle de acesso.
 */
export type Operador = {
  /** O MASTER (papel `admin`): passa por tudo, e é quem muda Admin › Permissões. */
  admin: boolean;
  /**
   * O nível de quem está logado em CADA aba, como o banco o calculou. É daqui
   * que o menu e os botões de edição saem — nunca de uma lista de papéis aqui
   * no front. Quem recusa continua sendo cada função.
   */
  areas: Record<string, NivelDeAcesso>;
  /** Tem acesso a ALGUMA coisa do painel (master, grupo ou exceção). */
  painel: boolean;
};

/** O nível numa aba; aba sem linha em `perm_areas` (Painel, Aplicativo…) é de admin. */
export function nivelNaAba(op: Operador, aba: string): NivelDeAcesso {
  if (op.admin) return "editar";
  return op.areas[aba] ?? "nenhum";
}

export type EstadoOperador =
  | { fase: "carregando" }
  | { fase: "erro" }
  | { fase: "pronto"; operador: Operador };

export function useOperador(): EstadoOperador {
  const [estado, setEstado] = useState<EstadoOperador>({ fase: "carregando" });

  useEffect(() => {
    let ativo = true;
    (async () => {
      // `minhas_permissoes` (000295) e não mais `meus_papeis`: desde a 000297
      // TODO operador que não é master entra pela concessão de um grupo.
      const { data, error } = await supabase.rpc("minhas_permissoes");
      if (!ativo) return;
      if (error || !data) {
        setEstado({ fase: "erro" });
        return;
      }
      const p = data as { admin: boolean; areas: Record<string, NivelDeAcesso> };
      const areas = p.areas ?? {};
      setEstado({
        fase: "pronto",
        operador: {
          admin: p.admin === true,
          areas,
          painel: p.admin === true || Object.values(areas).some((n) => n !== "nenhum"),
        },
      });
    })();
    return () => {
      ativo = false;
    };
  }, []);

  return estado;
}

/**
 * As métricas do dashboard, na ordem em que o banco as devolve.
 *
 * Só número que o hub é DONO. Postagem, resposta e visualização são do Fórum;
 * aula assistida é do Memberkit. Widget alimentado por dado que o hub não tem
 * destrói a credibilidade dos que têm dado real — a mesma regra que mantém
 * progresso e streak fora da tela do aluno.
 */
export type Painel = {
  alunos_com_acesso: number;
  perfis: number;
  contas_criadas: number;
  vinculos_pendentes: number;
  acessos_vigentes: number;
  acessos_vitalicios: number;
  acessos_vencem_30d: number;
  acessos_expirados: number;
  acessos_cancelados: number;
  eventos_hoje: number;
  eventos_7d: number;
  triagem_falhas: number;
  triagem_avisos: number;
  webhooks_rejeitados: number;
  contratos_assinados: number;
  produtos_exigem_contrato: number;
  mk_senha_pendente: number;
  mk_falhas: number;
  matriculas_ativas: number;
  produtos_ativos: number;
  cards_total: number;
  medido_em: string;
};

export type EstadoPainel =
  | { fase: "carregando" }
  | { fase: "erro" }
  // Zero linha é a resposta do banco para "quem chamou não é operador". Não é
  // erro, e distinguir os dois é o que permite a tela dizer a coisa certa: uma
  // pede login de operador, a outra pede tentar de novo.
  | { fase: "negado" }
  | { fase: "pronto"; painel: Painel };

export function usePainel(): { estado: EstadoPainel; recarregar: () => void; recarregando: boolean } {
  const [estado, setEstado] = useState<EstadoPainel>({ fase: "carregando" });
  const [recarregando, setRecarregando] = useState(false);

  const buscar = useCallback(async (silencioso: boolean) => {
    if (silencioso) setRecarregando(true);
    const { data, error } = await supabase.rpc("painel_admin");
    if (error) {
      setEstado({ fase: "erro" });
    } else {
      const linhas = (data ?? []) as Painel[];
      setEstado(linhas.length === 0 ? { fase: "negado" } : { fase: "pronto", painel: linhas[0] });
    }
    if (silencioso) setRecarregando(false);
  }, []);

  useEffect(() => {
    void buscar(false);
  }, [buscar]);

  return { estado, recarregar: () => void buscar(true), recarregando };
}

// ---------------------------------------------------------------------------
// Suporte: achar o aluno e ver o caso dele
// ---------------------------------------------------------------------------

export type AlunoEncontrado = {
  profile_id: string;
  email: string;
  nome: string | null;
  cpf: string | null;
  telefone: string | null;
  bubble_ref: string | null;
  tem_conta: boolean;
  email_confirmado: boolean;
  cursos_vigentes: number;
  /** Fontes vigentes. Pode ser MAIOR que `cursos_vigentes` e não é duplicidade. */
  acessos_vigentes: number;
  criado_em: string;
  total_encontrado: number;
};

export type FonteDeAcesso = {
  acesso_id: string;
  produto_slug: string;
  produto_nome: string;
  curso_slug: string | null;
  exige_contrato: boolean;
  origem: string;
  transacao_origem: string;
  inicio: string | null;
  fim: string | null;
  vitalicio: boolean;
  status: "vigente" | "expirado" | "cancelado";
  contrato_variante: string | null;
  bubble_ref: string | null;
  criado_em: string;
  atualizado_em: string;
};

export type Dossie = {
  perfil: {
    id: string;
    email: string;
    nome: string | null;
    cpf: string | null;
    telefone: string | null;
    bubble_ref: string | null;
    criado_em: string;
    atualizado_em: string;
    avatar_url: string | null;
    bio: string | null;
    redes: Record<string, string>;
    mk_senha_em: string | null;
    tem_conta: boolean;
    email_confirmed_at: string | null;
    last_sign_in_at: string | null;
  };
  papeis: string[];
  cursos: Array<{
    curso_slug: string;
    curso_nome: string;
    fim_efetivo: string | null;
    vitalicio: boolean;
    faculdade: boolean;
    produtos: string[];
    fontes: number;
    contrato_pendente: boolean;
    trancado: boolean;
    contrato_url: string | null;
  }>;
  efetivo: Array<{
    produto_slug: string;
    produto_nome: string;
    curso_slug: string | null;
    fim_efetivo: string | null;
    vitalicio: boolean;
    fontes: number;
  }>;
  fontes: FonteDeAcesso[];
  matriculas: Array<{
    id: string;
    produto_slug: string;
    produto_nome: string;
    tipo: string;
    turma: string | null;
    inicio: string | null;
    previsao_termino: string | null;
    status: string;
    bubble_ref: string | null;
  }>;
  contratos: Array<{
    produto_slug: string;
    produto_nome: string;
    curso_slug: string | null;
    status: string;
    assinado_em: string | null;
    zapsign_ref_assinado: string | null;
  }>;
  a_assinar: Array<{
    produto_slug: string;
    produto_nome: string;
    curso_slug: string | null;
    variante: string | null;
    contrato_url: string | null;
    de_variante: boolean;
  }>;
  eventos: Array<{
    id: number;
    origem: string;
    tipo: string;
    id_transacao: string;
    recebido_em: string;
    processado_em: string | null;
    erro: string | null;
    motivo: string | null;
  }>;
  correcoes: Array<{
    id: number;
    produto_slug: string | null;
    vitalicio_antes: boolean | null;
    fim_antes: string | null;
    vitalicio_depois: boolean | null;
    fim_depois: string | null;
    motivo: string;
    corrigido_em: string;
  }>;
  lido_em: string;
};

export type ResultadoBusca =
  | { fase: "ok"; itens: AlunoEncontrado[]; total: number }
  // O banco recusa termo com menos de 3 caracteres levantando exceção, e a
  // mensagem dele é escrita para ser lida por gente. Repassar em vez de
  // traduzir mantém uma única versão da regra.
  | { fase: "recusado"; mensagem: string }
  | { fase: "erro" };

export async function buscarAluno(termo: string): Promise<ResultadoBusca> {
  const { data, error } = await supabase.rpc("buscar_aluno", { p_termo: termo });
  if (error) {
    // 22023 = invalid_parameter_value, o errcode que a RPC usa para chamada
    // malformada. Qualquer outro é problema de verdade.
    if (error.code === "22023") return { fase: "recusado", mensagem: error.message };
    return { fase: "erro" };
  }
  const itens = (data ?? []) as AlunoEncontrado[];
  return { fase: "ok", itens, total: itens.length > 0 ? itens[0].total_encontrado : 0 };
}

export async function alunoDetalhe(profileId: string): Promise<Dossie | null> {
  const { data, error } = await supabase.rpc("aluno_detalhe", { p_profile_id: profileId });
  if (error) return null;
  return (data ?? null) as Dossie | null;
}

/**
 * O «ver como» (000335): abre a sessão e recarrega a página na inicial do hub,
 * já como a outra pessoa. Só volta se o banco RECUSOU — no caminho feliz, a
 * página é trocada antes de qualquer resposta chegar à tela.
 *
 * Recarregar, e não navegar: o `useHub` e as leituras já feitas estão em
 * memória com os dados do master, e é a recarga que garante que nada deles
 * apareça na tela da outra pessoa (nem o contrário, na volta). E o cliente do
 * Supabase só ganha o cabeçalho na carga do módulo (`lib/supabase.ts`).
 *
 * `voltarPara` é a tela de onde o master saiu, com o dossiê reaberto.
 */
export async function verComo(profileId: string, voltarPara: string): Promise<{ ok: false; mensagem: string } | undefined> {
  const { data, error } = await supabase.rpc("ver_como_iniciar", { p_alvo: profileId });
  if (error) return { ok: false, mensagem: "Não foi possível abrir o «ver como» agora. Tente de novo." };
  const r = data as {
    ok: boolean;
    detalhe?: string;
    sessao?: string;
    expira_em?: string;
    alvo?: { id: string; nome: string | null; email: string };
  };
  if (!r.ok || !r.sessao || !r.expira_em || !r.alvo) {
    return { ok: false, mensagem: r.detalhe ?? "O banco recusou o «ver como»." };
  }
  guardarSessaoVerComo({ token: r.sessao, alvo: r.alvo, expira_em: r.expira_em, voltar_para: voltarPara });
  window.location.assign("/");
  return undefined;
}

// ---------------------------------------------------------------------------
// Suporte: as três escritas sobre acesso
// ---------------------------------------------------------------------------

export type ResultadoPrazo = {
  acesso_id: string;
  produto_slug: string;
  vitalicio_antes: boolean;
  fim_antes: string | null;
  vitalicio_depois: boolean;
  fim_depois: string | null;
  /** Verdadeiro quando a correção ENCURTOU o acesso — o caso irreversível. */
  reduziu: boolean;
};

export type ResultadoCancelamento = {
  acesso_id: string;
  produto_slug: string;
  mudou: boolean;
  motivo_do_nao?: string;
  era_vitalicio?: boolean;
  fontes_restantes?: number;
  /** A consequência: outra compra ainda sustenta o produto, ou o aluno perdeu. */
  perdeu_o_produto?: boolean;
  fim_efetivo_restante?: string | null;
  vitalicio_restante?: boolean;
};

export type Escrita<T> = { ok: true; res: T } | { ok: false; mensagem: string };

/**
 * Converte a data que o operador digitou no fim daquele dia, no fuso de Brasília.
 *
 * `<input type="date">` devolve `2027-05-10`, e mandar isso para um `timestamptz`
 * grava meia-noite **UTC** — que é 21:00 do dia 09 em Brasília. O aluno perderia
 * o acesso três horas antes do dia que o operador digitou, e a reclamação
 * chegaria como "sumiu um dia antes".
 *
 * "Expira em 10/05/2027" quer dizer que o dia 10 ainda vale. Então 23:59:59 de
 * Brasília, e o `-03:00` é fixo: o Brasil não tem horário de verão desde 2019.
 */
export function fimDoDiaEmBrasilia(data: string): string {
  return `${data}T23:59:59-03:00`;
}

export async function corrigirPrazo(
  acessoId: string,
  novoFim: string | null,
  vitalicio: boolean,
  motivo: string,
): Promise<Escrita<ResultadoPrazo>> {
  const { data, error } = await supabase.rpc("corrigir_prazo_da_fonte", {
    p_acesso_id: acessoId,
    p_novo_fim: novoFim,
    p_vitalicio: vitalicio,
    p_motivo: motivo,
  });
  // As mensagens do banco são escritas para serem lidas por gente (data além de
  // 50 anos, motivo obrigatório). Repassar em vez de traduzir mantém uma única
  // versão de cada regra.
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoPrazo };
}

export async function cancelarFonte(
  acessoId: string,
  motivo: string,
): Promise<Escrita<ResultadoCancelamento>> {
  const { data, error } = await supabase.rpc("cancelar_fonte", {
    p_acesso_id: acessoId,
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoCancelamento };
}

/**
 * Libera um produto à mão. Devolve o id do EVENTO, não sucesso.
 *
 * A distinção é da 000009: a função pode gravar o evento **e** deixar um `erro`
 * nele (slug fora do catálogo), e o único desfecho que significa "acesso
 * concedido" é `erro is null and processado_em is not null`. O painel não lê
 * `eventos_provisionamento` — e não vai ler, é tabela de bastidor —, então quem
 * confere é a tela: ela recarrega o dossiê e vê se a fonte apareceu. Se não
 * apareceu, a liberação está na fila de triagem, e é isso que ela diz.
 */
export async function liberarAcesso(
  email: string,
  produtoSlug: string,
  motivo: string,
): Promise<Escrita<number>> {
  const { data, error } = await supabase.rpc("liberar_acesso_manual", {
    p_email: email,
    p_produto_slug: produtoSlug,
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as number };
}

// ---------------------------------------------------------------------------
// Suporte: a fila da triagem
// ---------------------------------------------------------------------------
//
// A fila NÃO é lida da `vw_triagem` pela Data API, e não é por comodidade:
// `authenticated` não tem grant nela, e não pode ter — ela alimenta o alerta de
// chat, e o teste 14 proíbe coluna de dado pessoal ali. Uma tela que a
// consultasse leria ZERO linha sem erro nenhum, que é a forma do defeito da
// 000052. Quem enriquece com o aluno é `triagem_fila()`, função de dono.

/** Uma linha da fila. As 19 colunas de `triagem_fila`, sem nada calculado aqui. */
export type LinhaTriagem = {
  fonte: "evento" | "webhook";
  ref_id: number;
  severidade: "falha" | "aviso";
  origem: string;
  tipo: string | null;
  id_transacao: string | null;
  motivo: string;
  /** O motivo com uuid e ids normalizados. É por ele que a tela agrupa. */
  motivo_classe: string;
  recebido_em: string;
  dias: number;
  /** Existe fonte em `acessos` para a transação citada — ou seja, o replay tem alvo. */
  tem_fonte_para_transacao: boolean;
  /** A MESMA regra que a RPC aplica. Se a tela e o banco discordarem, o botão mente. */
  pode_reprocessar: boolean;
  aluno_email: string | null;
  aluno_profile_id: string | null;
  /** De onde o aluno saiu. A tela não pode afirmar aluno sem dizer a procedência. */
  aluno_procedencia: "canonico" | "acessos" | "bruto" | "nao_identificado";
  produtos: string | null;
  arquivada_em: string | null;
  arquivada_por: string | null;
  arquivada_motivo: string | null;
};

export type EstadoTriagem =
  | { fase: "carregando" }
  | { fase: "erro" }
  /** Zero linha é a resposta do banco a "não é operador" — distinta de erro. */
  | { fase: "negado" }
  | { fase: "pronto"; linhas: LinhaTriagem[]; lidoEm: string };

export function useTriagem(arquivadas: boolean): {
  estado: EstadoTriagem;
  recarregar: () => void;
  recarregando: boolean;
} {
  const [estado, setEstado] = useState<EstadoTriagem>({ fase: "carregando" });
  const [recarregando, setRecarregando] = useState(false);

  const buscar = useCallback(
    async (silencioso: boolean) => {
      if (silencioso) setRecarregando(true);
      const { data, error } = await supabase.rpc("triagem_fila", { p_arquivadas: arquivadas });
      if (error) {
        setEstado({ fase: "erro" });
      } else {
        const linhas = (data ?? []) as LinhaTriagem[];
        setEstado({ fase: "pronto", linhas, lidoEm: new Date().toISOString() });
      }
      if (silencioso) setRecarregando(false);
    },
    [arquivadas],
  );

  useEffect(() => {
    void buscar(false);
  }, [buscar]);

  return { estado, recarregar: () => void buscar(true), recarregando };
}

export type ResultadoArquivamento = {
  arquivada?: boolean;
  desarquivada?: boolean;
  fonte: string;
  ref_id: number;
  severidade?: string;
  motivo_arquivado?: string;
  abertas_restantes: number;
};

/**
 * O que `reprocessar_pendencia` devolve — e por que não é um booleano.
 *
 * `replay_evento` devolve `text`, e `null` significa DUAS coisas: "recusei em
 * silêncio" e "reapliquei e deu certo". A RPC relê a linha depois e devolve o que
 * ACONTECEU, para a tela nunca dizer "sucesso" quando nada mudou.
 */
export type ResultadoReprocessamento = {
  aplicou: boolean;
  saiu_da_fila: boolean;
  erro_anterior: string | null;
  erro_novo: string | null;
  erro_igual: boolean;
  severidade_nova: "falha" | "aviso" | null;
};

export async function arquivarPendencia(
  fonte: string,
  refId: number,
  motivo: string,
): Promise<Escrita<ResultadoArquivamento>> {
  const { data, error } = await supabase.rpc("arquivar_pendencia", {
    p_fonte: fonte,
    p_ref_id: refId,
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoArquivamento };
}

export async function desarquivarPendencia(
  fonte: string,
  refId: number,
  motivo: string,
): Promise<Escrita<ResultadoArquivamento>> {
  const { data, error } = await supabase.rpc("desarquivar_pendencia", {
    p_fonte: fonte,
    p_ref_id: refId,
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoArquivamento };
}

/**
 * Reprocessa uma pendência. NÃO existe parâmetro de forçar, e a ausência é a
 * trava: forçar o replay de uma compra estornada desfaz o estorno.
 */
export async function reprocessarPendencia(
  refId: number,
  motivo: string,
): Promise<Escrita<ResultadoReprocessamento>> {
  const { data, error } = await supabase.rpc("reprocessar_pendencia", {
    p_ref_id: refId,
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoReprocessamento };
}

// ---------------------------------------------------------------------------
// Suporte: cadastro, papéis, criação e primeiro acesso
// ---------------------------------------------------------------------------

export type ResultadoCadastro = {
  profile_id: string;
  mudou: boolean;
  campos: Record<string, { antes: string | null; depois: string | null }>;
};

/**
 * Nulo em qualquer campo quer dizer "não mexe"; string vazia quer dizer "apaga".
 *
 * A convenção é a mesma do perfil do aluno (000042/000043) e existe por um motivo
 * prático: sem ela, um formulário que salva só o telefone apagaria o CPF que
 * alguém conferiu no documento.
 */
export async function atualizarCadastro(
  profileId: string,
  campos: { nome?: string | null; telefone?: string | null; cpf?: string | null },
  motivo: string,
): Promise<Escrita<ResultadoCadastro>> {
  const { data, error } = await supabase.rpc("atualizar_cadastro_do_aluno", {
    p_profile_id: profileId,
    p_nome: campos.nome ?? null,
    p_telefone: campos.telefone ?? null,
    p_cpf: campos.cpf ?? null,
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoCadastro };
}

export type ResultadoCriacao = {
  profile_id: string;
  criou: boolean;
  motivo_do_nao?: string;
};

/**
 * Cria o cadastro, SEM papel: desde a 000297 o acesso de operador é por grupo,
 * em Admin › Permissões, e o banco recusa quem manda papel aqui (quem mandava
 * achava que estava dando acesso). A lista vai vazia por isso.
 */
export async function criarAluno(
  email: string,
  nome: string,
  motivo: string,
): Promise<Escrita<ResultadoCriacao>> {
  const { data, error } = await supabase.rpc("criar_aluno", {
    p_email: email,
    p_nome: nome,
    p_papeis: [],
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoCriacao };
}

export type ResultadoPrimeiroAcesso = {
  resultado: "convite_enviado" | "redefinicao_enviada" | "falhou";
  email?: string;
};

/**
 * Manda o convite (quem não tem conta) ou o link de redefinição (quem tem).
 *
 * Passa por Edge Function porque só o Auth emite esses e-mails, e isso pede a
 * service key — que nunca vai para o navegador. **O link não volta para cá**: um
 * link de redefinição é primitiva de tomada de conta, e devolvê-lo transformaria
 * "ajudar o aluno a entrar" em "ver a conta do aluno". O e-mail vai do Auth para
 * a caixa do dono; o operador recebe só o desfecho.
 */
export async function liberarPrimeiroAcesso(
  profileId: string,
  motivo: string,
): Promise<Escrita<ResultadoPrimeiroAcesso>> {
  const { data, error } = await supabase.functions.invoke("liberar-primeiro-acesso", {
    body: { profile_id: profileId, motivo },
  });
  if (error) {
    // O corpo do erro da function carrega a mensagem útil (o Auth recusou, hub
    // sem endereço configurado). Sem lê-lo, o operador veria só "erro".
    const detalhe =
      typeof data === "object" && data !== null && "erro" in data
        ? String((data as { erro: unknown }).erro)
        : error.message;
    return { ok: false, mensagem: detalhe };
  }
  return { ok: true, res: data as ResultadoPrimeiroAcesso };
}

export type ResultadoSenhaLiberada = {
  liberada: boolean;
  ja_estava: boolean;
  vale_ate: string;
};

export type ResultadoCancelamentoDaSenha = {
  cancelada: boolean;
  motivo_do_nao?: string;
};

/** A última liberação de senha sem e-mail do aluno, ou nulo quando nunca houve. */
export type SituacaoDaSenhaLiberada = {
  liberada_em: string;
  vale_ate: string;
  usada_em: string | null;
  cancelada_em: string | null;
  liberada_por: string | null;
  valendo: boolean;
} | null;

/**
 * Libera o aluno para criar a senha sem e-mail, em `/senha-liberada` (000383).
 *
 * É o caminho de quem não recebe o e-mail do Auth: a liberação vale 1 hora e uma
 * vez só, e o PRÓPRIO aluno digita a senha. O operador não vê nem digita senha
 * nenhuma, e nenhum link sai daqui. O banco recusa a conta da equipe: liberar a
 * de um master daria a quem edita a aba Alunos os poderes dele.
 */
export async function liberarSenhaSemEmail(
  profileId: string,
  motivo: string,
): Promise<Escrita<ResultadoSenhaLiberada>> {
  const { data, error } = await supabase.rpc("liberar_senha_sem_email", {
    p_aluno: profileId,
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoSenhaLiberada };
}

export async function cancelarSenhaLiberada(
  profileId: string,
  motivo: string,
): Promise<Escrita<ResultadoCancelamentoDaSenha>> {
  const { data, error } = await supabase.rpc("cancelar_senha_liberada", {
    p_aluno: profileId,
    p_motivo: motivo,
  });
  if (error) return { ok: false, mensagem: error.message };
  return { ok: true, res: data as ResultadoCancelamentoDaSenha };
}

/**
 * Devolve `undefined` quando a leitura falhou, para a tela não confundir "não deu
 * para saber" com "nunca foi liberado".
 */
export async function senhaLiberadaDoAluno(
  profileId: string,
): Promise<SituacaoDaSenhaLiberada | undefined> {
  const { data, error } = await supabase.rpc("senha_liberada_do_aluno", { p_aluno: profileId });
  if (error) return undefined;
  return (data ?? null) as SituacaoDaSenhaLiberada;
}

export type ResultadoTrocaDeEmail = {
  resultado: "trocado" | "trocado_sem_conta_no_auth";
  de: string;
  para: string;
};

/**
 * Troca o e-mail do aluno — no cadastro e, quando ele já tem conta, no Auth.
 *
 * Passa por Edge Function, e não por RPC direta, porque o lado do Auth exige a
 * API admin: `auth.identities` guarda o e-mail em duas colunas, e mexer só em
 * `auth.users` por SQL deixaria o login inconsistente. A autorização e a
 * auditoria continuam no BANCO (`trocar_email_do_aluno` exige `pode_editar('alunos')` e
 * assina com `meu_profile_id()`) — a function só orquestra as duas escritas.
 *
 * As mensagens de recusa vêm da RPC e são escritas para o operador ler ("o
 * e-mail X já é do aluno Y"), então são repassadas como estão.
 */
export async function trocarEmailDoAluno(
  profileId: string,
  emailNovo: string,
  motivo: string,
): Promise<Escrita<ResultadoTrocaDeEmail>> {
  const { data, error } = await supabase.functions.invoke("trocar-email-do-aluno", {
    body: { profile_id: profileId, email_novo: emailNovo, motivo },
  });
  if (error) {
    const detalhe =
      typeof data === "object" && data !== null && "erro" in data
        ? String((data as { erro: unknown }).erro)
        : error.message;
    return { ok: false, mensagem: detalhe };
  }
  return { ok: true, res: data as ResultadoTrocaDeEmail };
}

export async function produtosDoCatalogo(): Promise<Array<{ slug: string; nome: string }>> {
  const { data, error } = await supabase
    .from("produtos")
    .select("slug, nome")
    .eq("ativo", true)
    .order("nome");
  if (error) return [];
  return (data ?? []) as Array<{ slug: string; nome: string }>;
}

/** Milhar com ponto, como o público brasileiro lê. */
export function numero(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return "—";
  return new Intl.NumberFormat("pt-BR").format(valor);
}

/**
 * A hora do carimbo do dado, no fuso de quem opera.
 *
 * `medido_em` vem do banco, que roda em UTC. Mostrar a hora crua faria o
 * operador ler "18:45" às 15:45 e concluir que o painel está adiantado — ou
 * pior, que o número é de outra hora.
 */
export function horaDoDado(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

/**
 * Data e hora no fuso de quem opera, ou um travessão.
 *
 * Sempre no fuso de Brasília, pelo mesmo motivo de `horaDoDado`: o banco guarda
 * em UTC, e o operador que vê "03:00" numa venda feita meia-noite conclui coisa
 * errada sobre quando ela chegou.
 *
 * Data **nula é vitalício** em `acessos.fim`, e quem decide o rótulo é a tela que
 * conhece o contexto — aqui o travessão é só ausência de valor.
 */
export function dataHora(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

/** Só a data, para colunas onde a hora é ruído. */
export function dataCurta(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}
